import { TaskType, TaskTypeValues } from "@remi/enums/task-type";
import { TaskTypesWithHistoryUi } from "@remi/task-attempt-types";
import { isOneOf } from "@remi/ts-utils";
import {
  BlockingTaskResponsibleParty,
  BlockingTaskTypeValues,
  ResponsiblePartiesBlockingTasks,
  ResponsibleParty,
} from "@remi/types/tasks";

import { hasCapability, type User } from "../base";
import { NON_EXISTENT_ID } from "../utils/types";
import {
  canUserManageWorkOrderForTask,
  canUserReadTaskType,
  canUserReviewWorkOrder,
  canUserReviewWorkOrderForTask,
  canUserUpdateTask,
  type TaskForPermissionCheck,
} from "./task-utils";

const ContractorResolvableBlockerTaskTypes = [
  TaskType.HOMEOWNER_UNRESPONSIVE,
  TaskType.OTHER_WORK_MUST_FINISH_FIRST,
] as const;

export const DrawerOnlyCompletionTaskTypes = [
  TaskType.REVIEW_WORK_ORDER_REJECTION,
  TaskType.APPROVE_WORK_ORDER_CONCESSION,
  TaskType.REQUOTE_OR_ABANDON_SOW_CHANGE,
  TaskType.SOW_CHANGE_REVIEW,
  TaskType.SOW_CHANGE_FINALIZE_QUOTE,
  TaskType.SOW_CHANGE_SEND_ADDENDUM,
  TaskType.SOW_CHANGE_VERIFY,
  TaskType.REVIEW_CORRECTIONS_FLAG,
  TaskType.SOW_CHANGE_SUPPLEMENT_ESTIMATE,
  TaskType.SOW_CHANGE_SUPPLEMENT_ESTIMATE_REVIEW,
  TaskType.SOW_CHANGE_CARRIER_APPROVAL,
  TaskType.SOW_CHANGE_ELECTIVE_ESTIMATE,
  TaskType.SOW_CHANGE_ELECTIVE_ESTIMATE_REVIEW,
  TaskType.SOW_CHANGE_CREATE_WORK_ORDERS,
] as const;

const ChangeOrderForceCompleteTaskTypes = [
  TaskType.REVIEW_CHANGE_ORDER_REQUEST,
  TaskType.FINALIZE_AMOUNT_COVERED_BY_BUYER,
  TaskType.FINALIZE_QUOTE,
] as const;

const ContractorAccessibleResponsibleParties = [
  ResponsibleParty.CONTRACTOR,
  ResponsibleParty.AHJ,
] as const satisfies ResponsibleParty[];

export const isContractorAccessibleBlockingTask = (task: { type: TaskType; isDeleted?: boolean }): boolean =>
  task.isDeleted !== true &&
  isOneOf(task.type, BlockingTaskTypeValues) &&
  isOneOf(BlockingTaskResponsibleParty[task.type], ContractorAccessibleResponsibleParties);

export const isContractorResolvableBlockerType = (type: TaskType): boolean =>
  isContractorAccessibleBlockingTask({ type }) || isOneOf(type, ContractorResolvableBlockerTaskTypes);

const mapUserToResponsibleParties = (user: User): readonly ResponsibleParty[] => {
  const parties: ResponsibleParty[] = [];
  if (hasCapability(user, "blockingTask.responsibleParty.buyer")) {
    parties.push(ResponsibleParty.BUYER);
  }
  if (hasCapability(user, "blockingTask.responsibleParty.contractor")) {
    parties.push(...ContractorAccessibleResponsibleParties);
  }
  if (hasCapability(user, "blockingTask.responsibleParty.homeowner")) {
    parties.push(ResponsibleParty.HOMEOWNER);
  }
  return parties;
};

const canUserAccessBlockingTask = (user: User, task: TaskForPermissionCheck) => {
  if (isOneOf(task.type, BlockingTaskTypeValues)) {
    return mapUserToResponsibleParties(user).includes(BlockingTaskResponsibleParty[task.type]);
  }
  return false;
};

const canUpdateTask = (user: User, task: TaskForPermissionCheck) =>
  canUserAccessBlockingTask(user, task) || canUserUpdateTask(user, task);

// Advancing a task through its own flow — attempts, status changes, completion — as opposed to
// administering it. Reassign, reschedule and the other ~20 `update` endpoints stay on `update`
// alone, so the work-order specialist reaches the review flow and nothing else.
const canAdvanceTask = (user: User, task: TaskForPermissionCheck) =>
  canUpdateTask(user, task) || canUserReviewWorkOrderForTask(user, task);

export const task = {
  read: (user: User, task: TaskForPermissionCheck) => {
    return canUserAccessBlockingTask(user, task) || canUserReadTaskType(user, task.type);
  },
  update: canUpdateTask,
  advance: canAdvanceTask,
  // Deliberately disjoint from `update`, mirroring `forceComplete`: ~20 endpoints share
  // `can.task.update`, so granting the contractor resolve there would also hand them
  // completeTaskAndEvaluateProject (reason optional) and every other update-gated route.
  resolveAllowlistedBlocker: (user: User, taskType: TaskType) =>
    canUserAccessBlockingTask(user, { type: taskType }) ||
    (isOneOf(taskType, ContractorResolvableBlockerTaskTypes) && hasCapability(user, "task.update.contractorAdmin")),
  reviewWorkOrder: canUserReviewWorkOrder,
  reviewWorkOrderForTask: canUserReviewWorkOrderForTask,
  // Disjoint from `update` on purpose: rejecting a work order clears the active work order, resets
  // CONFIRM_CONTRACTOR and soft-deletes downstream tasks, so it stays on the work order gate alone.
  rejectWorkOrder: canUserManageWorkOrderForTask,
  // Generic completion skips the accept handler that writes schedule dates and permit linkage, so
  // REVIEW_AND_ACCEPT_WORK_ORDER stays on the work order gate here; accepting goes through attemptTask.
  // Drawer-only types close only through their own outcomes: a generic completion would record no decision,
  // release a decision type's hold with the decision unmade, skip a concession review's work order regeneration,
  // or skip the edits and checks a SOW change step's attempt makes.
  complete: (user: User, task: TaskForPermissionCheck): boolean => {
    if (isOneOf(task.type, DrawerOnlyCompletionTaskTypes)) {
      return false;
    }
    return task.type === TaskType.REVIEW_AND_ACCEPT_WORK_ORDER
      ? canUserManageWorkOrderForTask(user, task)
      : canAdvanceTask(user, task);
  },
  recordAttempt: (user: User) => hasCapability(user, "task.recordAttempt"),
  recordInternalAttemptReason: (user: User) => hasCapability(user, "task.recordAttempt.internalReasons"),
  viewUnscopedActivity: (user: User) => hasCapability(user, "task.read.all"),
  createPostInstallTask: (user: User) => hasCapability(user, "task.createPostInstallTask"),
  updateAHJRequirements: (user: User) => hasCapability(user, "task.updateAHJRequirements"),
  cancelPermit: (user: User, task: TaskForPermissionCheck) =>
    hasCapability(user, "task.updateAHJRequirements") ||
    (task.type === TaskType.PERMIT_COMPLETED && canAdvanceTask(user, task)),
  delete: (user: User) => hasCapability(user, "task.delete"),
  forceDelete: (user: User) => hasCapability(user, "task.forceDelete"),
  forceComplete: (user: User, taskType?: TaskType) => {
    if (isOneOf(taskType, DrawerOnlyCompletionTaskTypes)) {
      return false;
    }
    // Send Agreement's completion effects reopen Verify NTP, so closing it goes through bypassSendAgreement,
    // which asks whether a new agreement was actually signed (GAD-939).
    if (taskType === TaskType.RECORD_UPSELL_RESULTS) {
      return false;
    }
    if (hasCapability(user, "task.forceComplete")) {
      return true;
    }
    if (taskType === TaskType.ORDER_MATERIALS && hasCapability(user, "task.orderMaterials.forceComplete")) {
      return true;
    }
    return (
      isOneOf(taskType, ChangeOrderForceCompleteTaskTypes) && hasCapability(user, "task.changeOrder.forceComplete")
    );
  },
  // Same users who could force-complete Send Agreement before it moved to this action.
  bypassSendAgreement: (user: User) =>
    hasCapability(user, "task.forceComplete") || hasCapability(user, "task.changeOrder.forceComplete"),
  review: (user: User) => hasCapability(user, "task.review"),
  keepAsIs: (user: User) => hasCapability(user, "task.keepAsIs"),
  viewAttemptHistory: (user: User, taskType: TaskType, attemptEligible: boolean): boolean => {
    if (!hasCapability(user, "task.viewAttemptHistory")) {
      return false;
    }
    if (!attemptEligible && !isOneOf(taskType, TaskTypesWithHistoryUi)) {
      return false;
    }
    return canUserReadTaskType(user, taskType);
  },
  viewTaskMenu: (user: User) => hasCapability(user, "task.viewTaskMenu"),
};

export const deletedTask = {
  read: (user: User) => hasCapability(user, "deletedTask.read"),
  update: (user: User) => hasCapability(user, "deletedTask.update"),
};

export const taskDueDate = {
  update: (user: User) => hasCapability(user, "taskDueDate.update"),
};

export const accessibleTasks = (
  action: Exclude<
    keyof typeof task,
    "forceComplete" | "viewAttemptHistory" | "resolveAllowlistedBlocker" | "reviewWorkOrder"
  >,
  user: User,
) => TaskTypeValues.filter((type: TaskType) => task[action](user, { type }));

// No Prisma.TaskWhereInput return annotation — callers spread this into
// `findUnique({ where: { id: taskId, ...taskWhere(user) } })` which expects
// TaskWhereUniqueInput (narrower union on `id`). The inferred return type
// stays compatible with both unique and non-unique call sites.
export const taskWhere = (user: User) => {
  const readableTypes = accessibleTasks("read", user);
  const blockingTypes = mapUserToResponsibleParties(user).flatMap((party) => ResponsiblePartiesBlockingTasks[party]);

  if (readableTypes.length === 0 && blockingTypes.length === 0) {
    return { AND: [{ id: NON_EXISTENT_ID }] };
  }

  return {
    OR: [{ type: { in: readableTypes } }, { type: { in: blockingTypes } }],
  };
};
